import {DetailedError, parseResponse} from 'hono/client';
import {err, ok} from 'framework';
import type {Page, Result} from 'framework';
import type {RpcClient} from '@/infrastructure/rpc/rpc-client';
import type {
  AssistantRepository,
  DeleteAttachmentInput,
  ListConversationsInput,
  ProposalDecisionInput,
  UploadAttachmentInput,
} from '../../application/ports/assistant.repository';
import type {AssistantAttachment} from '../../domain/assistant-attachment';
import type {AssistantProposal} from '../../domain/assistant-proposal';
import type {AssistantError} from '../../domain/errors';
import {
  AttachmentLimitError,
  AttachmentNotFoundError,
  ConversationNotFoundError,
  InvalidAssistantInputError,
  NetworkError,
  ProposalNotFoundError,
  ProposalNotPendingError,
  ServerError,
  UnauthorizedError,
} from '../../domain/errors';
import type {Conversation, ConversationDetail} from '../../domain/conversation';
import type {ConversationId} from '../../domain/conversation-id.value-object';
import type {ConversationDtoToConversationMapper} from '../mappers/conversation-dto-to-conversation.mapper';
import type {ConversationDetailDtoToConversationDetailMapper} from '../mappers/conversation-detail-dto-to-conversation-detail.mapper';
import type {AssistantProposalDtoToAssistantProposalMapper} from '../mappers/assistant-proposal-dto-to-assistant-proposal.mapper';
import type {AssistantAttachmentDtoToAssistantAttachmentMapper} from '../mappers/assistant-attachment-dto-to-assistant-attachment.mapper';

export class RpcAssistantRepository implements AssistantRepository {
  constructor(
    private readonly rpcClient: RpcClient,
    private readonly conversationMapper: ConversationDtoToConversationMapper,
    private readonly detailMapper: ConversationDetailDtoToConversationDetailMapper,
    private readonly proposalMapper: AssistantProposalDtoToAssistantProposalMapper,
    private readonly attachmentMapper: AssistantAttachmentDtoToAssistantAttachmentMapper,
  ) {}

  async list(input: ListConversationsInput): Promise<Result<Page<Conversation>, AssistantError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient.assistant.conversations.$get({
          query: {
            ...(input.cursor !== null && {cursor: input.cursor}),
          },
        }),
      );
      if ('error' in dto || dto.meta === undefined) {
        return err(new ServerError());
      }
      return ok({
        items: dto.data.map(item => this.conversationMapper.transform(item)),
        nextCursor: dto.meta.nextCursor,
        hasMore: dto.meta.hasMore,
      });
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async getById(id: ConversationId): Promise<Result<ConversationDetail, AssistantError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient.assistant.conversations[':conversationId'].$get({
          param: {conversationId: id},
        }),
      );
      return ok(this.detailMapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async create(): Promise<Result<Conversation, AssistantError>> {
    try {
      const dto = await parseResponse(this.rpcClient.assistant.conversations.$post());
      return ok(this.conversationMapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async delete(id: ConversationId): Promise<Result<void, AssistantError>> {
    try {
      await parseResponse(
        this.rpcClient.assistant.conversations[':conversationId'].$delete({
          param: {conversationId: id},
        }),
      );
      return ok(undefined);
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async applyProposal(
    input: ProposalDecisionInput,
  ): Promise<Result<AssistantProposal, AssistantError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient.assistant.conversations[':conversationId'].proposals[
          ':proposalId'
        ].apply.$post({
          param: {
            conversationId: input.conversationId,
            proposalId: input.proposalId,
          },
        }),
      );
      return ok(this.proposalMapper.transform(dto));
    } catch (cause) {
      return this.mapProposalError(cause);
    }
  }

  async discardProposal(
    input: ProposalDecisionInput,
  ): Promise<Result<AssistantProposal, AssistantError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient.assistant.conversations[':conversationId'].proposals[
          ':proposalId'
        ].discard.$post({
          param: {
            conversationId: input.conversationId,
            proposalId: input.proposalId,
          },
        }),
      );
      return ok(this.proposalMapper.transform(dto));
    } catch (cause) {
      return this.mapProposalError(cause);
    }
  }

  async uploadAttachment(
    input: UploadAttachmentInput,
  ): Promise<Result<AssistantAttachment, AssistantError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient.assistant.conversations[':conversationId'].attachments.$post({
          param: {conversationId: input.conversationId},
          form: {image: input.file},
        }),
      );
      return ok(this.attachmentMapper.transform(dto));
    } catch (cause) {
      return this.mapAttachmentError(cause, 'upload');
    }
  }

  async deleteAttachment(input: DeleteAttachmentInput): Promise<Result<void, AssistantError>> {
    try {
      await parseResponse(
        this.rpcClient.assistant.conversations[':conversationId'].attachments[
          ':attachmentId'
        ].$delete({
          param: {conversationId: input.conversationId, attachmentId: input.attachmentId},
        }),
      );
      return ok(undefined);
    } catch (cause) {
      return this.mapAttachmentError(cause, 'delete');
    }
  }

  /**
   * 409 is the per-message photo cap; 413 the byte cap; on delete a 404 is
   * the photo (the conversation was found first), on upload it is the
   * conversation.
   */
  private mapAttachmentError(
    cause: unknown,
    operation: 'upload' | 'delete',
  ): Result<never, AssistantError> {
    if (cause instanceof DetailedError) {
      if (cause.statusCode === 409) {
        return err(new AttachmentLimitError());
      }
      if (cause.statusCode === 413) {
        return err(new InvalidAssistantInputError('The photo is too large.'));
      }
      if (cause.statusCode === 404 && operation === 'delete') {
        return err(new AttachmentNotFoundError());
      }
    }
    return this.mapError(cause);
  }

  private mapError(cause: unknown): Result<never, AssistantError> {
    if (cause instanceof DetailedError) {
      if (cause.statusCode === 401) {
        return err(new UnauthorizedError());
      }
      if (cause.statusCode === 404) {
        return err(new ConversationNotFoundError());
      }
      if (cause.statusCode === 400 || cause.statusCode === 422) {
        return err(new InvalidAssistantInputError());
      }
      return err(new ServerError());
    }
    return err(new NetworkError());
  }

  /** 404 is the proposal (the conversation was found first) and 409 a decision that already happened. */
  private mapProposalError(cause: unknown): Result<never, AssistantError> {
    if (cause instanceof DetailedError) {
      if (cause.statusCode === 404) {
        return err(new ProposalNotFoundError());
      }
      if (cause.statusCode === 409) {
        return err(new ProposalNotPendingError());
      }
    }
    return this.mapError(cause);
  }
}
